/**
 * Client météo Open-Meteo (https://open-meteo.com) — couverture mondiale.
 * Pas de clé API (appel direct depuis le front). Deux endpoints :
 * - géocodage : recherche d'une ville → lat/lon
 * - forecast  : conditions actuelles + prévisions horaires et journalières
 *
 * NB : l'usage commercial nécessite un abonnement Open-Meteo (voir leurs CGU).
 */

const GEOCODING_URL = "https://geocoding-api.open-meteo.com/v1/search";
const FORECAST_URL = "https://api.open-meteo.com/v1/forecast";

export type GeoResult = {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  country?: string;
  countryCode?: string;
  admin1?: string;
};

export type WeatherCurrent = {
  temperature: number;
  apparentTemperature: number;
  humidity: number;
  precipitation: number;
  weatherCode: number;
  windSpeed: number;
  windDirection: number;
  windGusts: number;
  pressure: number;
  dewPoint: number;
  uvIndex: number;
  isDay: boolean;
  time: string;
};

export type WeatherHour = {
  time: string;
  temperature: number;
  weatherCode: number;
};

export type WeatherDay = {
  date: string;
  weatherCode: number;
  tempMax: number;
  tempMin: number;
  precipitationProbability: number | null;
  sunrise: string | null;
  sunset: string | null;
};

export type WeatherData = {
  current: WeatherCurrent;
  hourly: WeatherHour[];
  daily: WeatherDay[];
  timezone: string;
};

/** Recherche de villes dans le monde entier (autocomplétion). */
export async function searchCities(
  query: string,
  signal?: AbortSignal
): Promise<GeoResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const url = `${GEOCODING_URL}?name=${encodeURIComponent(
    q
  )}&count=8&language=fr&format=json`;
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data = await res.json();
  return (data.results ?? []).map(
    (r: {
      id: number;
      name: string;
      latitude: number;
      longitude: number;
      country?: string;
      country_code?: string;
      admin1?: string;
    }) => ({
      id: r.id,
      name: r.name,
      latitude: r.latitude,
      longitude: r.longitude,
      country: r.country,
      countryCode: r.country_code,
      admin1: r.admin1,
    })
  );
}

/** Conditions actuelles + prévisions (7 jours) pour une localisation. */
async function fetchWeatherNetwork(
  latitude: number,
  longitude: number,
  signal?: AbortSignal
): Promise<WeatherData> {
  const params = new URLSearchParams({
    latitude: String(latitude),
    longitude: String(longitude),
    current:
      "temperature_2m,relative_humidity_2m,apparent_temperature,is_day,precipitation,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m,pressure_msl",
    // dew_point_2m et uv_index ne sont pas dans `current` : on les lit dans l'horaire.
    hourly: "temperature_2m,weather_code,dew_point_2m,uv_index",
    daily:
      "weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,sunrise,sunset",
    timezone: "auto",
    forecast_days: "7",
  });

  const res = await fetch(`${FORECAST_URL}?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const d = await res.json();
  const c = d.current;

  const hTimes: string[] = d.hourly?.time ?? [];
  const hTemps: number[] = d.hourly?.temperature_2m ?? [];
  const hCodes: number[] = d.hourly?.weather_code ?? [];
  const hDew: number[] = d.hourly?.dew_point_2m ?? [];
  const hUv: number[] = d.hourly?.uv_index ?? [];

  // Index de l'heure courante (dernière heure <= maintenant) pour rosée & UV.
  let currentHourIdx = 0;
  for (let i = 0; i < hTimes.length; i++) {
    if (hTimes[i] <= c.time) currentHourIdx = i;
    else break;
  }

  const current: WeatherCurrent = {
    temperature: c.temperature_2m,
    apparentTemperature: c.apparent_temperature,
    humidity: c.relative_humidity_2m,
    precipitation: c.precipitation,
    weatherCode: c.weather_code,
    windSpeed: c.wind_speed_10m,
    windDirection: c.wind_direction_10m,
    windGusts: c.wind_gusts_10m,
    pressure: c.pressure_msl,
    dewPoint: hDew[currentHourIdx] ?? c.temperature_2m,
    uvIndex: hUv[currentHourIdx] ?? 0,
    isDay: c.is_day === 1,
    time: c.time,
  };

  // Horaire : on ne garde que les prochaines heures à partir de maintenant.
  const startIdx = Math.max(
    0,
    hTimes.findIndex((t) => t >= c.time)
  );
  const hourly: WeatherHour[] = hTimes
    .slice(startIdx, startIdx + 12)
    .map((time, i) => ({
      time,
      temperature: hTemps[startIdx + i],
      weatherCode: hCodes[startIdx + i],
    }));

  const dTimes: string[] = d.daily?.time ?? [];
  const daily: WeatherDay[] = dTimes.map((date, i) => ({
    date,
    weatherCode: d.daily.weather_code[i],
    tempMax: d.daily.temperature_2m_max[i],
    tempMin: d.daily.temperature_2m_min[i],
    precipitationProbability: d.daily.precipitation_probability_max?.[i] ?? null,
    sunrise: d.daily.sunrise?.[i] ?? null,
    sunset: d.daily.sunset?.[i] ?? null,
  }));

  return { current, hourly, daily, timezone: d.timezone };
}

// ── Cache météo (TTL) ────────────────────────────────────────────────────────
// On évite de rappeler Open-Meteo à chaque montage / reload : une donnée fraîche
// (< TTL) est réutilisée. Persisté en sessionStorage → survit aux navigations
// internes ET au F5 dans le même onglet, tout en restant propre à la session.

export type CachedWeather = { data: WeatherData; fetchedAt: number };

/** Durée de validité d'une donnée météo (12 min, entre 10 et 15). */
const WEATHER_TTL_MS = 12 * 60 * 1000;
const CACHE_PREFIX = "weather-cache:";

/** Clé de cache : coordonnées arrondies (~1 km) pour regrouper les quasi-identiques. */
function cacheKey(latitude: number, longitude: number): string {
  return `${CACHE_PREFIX}${latitude.toFixed(2)},${longitude.toFixed(2)}`;
}

function readCache(key: string): CachedWeather | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as CachedWeather;
  } catch {
    return null;
  }
}

function writeCache(key: string, entry: CachedWeather): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Quota plein / mode privé : on ignore (le cache est best-effort).
  }
}

// Requêtes en vol : dédupe les appels simultanés pour une même localisation
// (ex. plusieurs widgets météo de la même ville montés au même moment via lazyLoad).
const inFlight = new Map<string, Promise<CachedWeather>>();

/**
 * Récupère la météo avec cache TTL. Retourne la donnée + l'horodatage du fetch
 * réel (pour afficher « Mis à jour à … »). Force le rafraîchissement avec `force`.
 */
export async function fetchWeather(
  latitude: number,
  longitude: number,
  signal?: AbortSignal,
  force = false
): Promise<CachedWeather> {
  const key = cacheKey(latitude, longitude);

  if (!force) {
    const cached = readCache(key);
    if (cached && Date.now() - cached.fetchedAt < WEATHER_TTL_MS) {
      return cached;
    }
    const pending = inFlight.get(key);
    if (pending) return pending;
  }

  // NB : on ne passe pas `signal` à la requête partagée — un consommateur qui se
  // démonte ne doit pas annuler la requête pour les autres. L'abort est géré côté
  // appelant (il ignore le résultat). Le `signal` reste accepté pour compat.
  void signal;

  const request = fetchWeatherNetwork(latitude, longitude)
    .then((data) => {
      const entry: CachedWeather = { data, fetchedAt: Date.now() };
      writeCache(key, entry);
      return entry;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
}

/** Formate un résultat de géocodage en libellé lisible (ex. "Quimper, Bretagne, FR"). */
export function formatGeoResult(geo: GeoResult): string {
  return [geo.name, geo.admin1, geo.countryCode].filter(Boolean).join(", ");
}

/** Région + pays d'un résultat (ex. "Bretagne, France"), pour l'affichage secondaire. */
export function formatGeoRegion(geo: GeoResult): string {
  return [geo.admin1, geo.country].filter(Boolean).join(", ");
}

/** Convertit un code pays ISO 3166-1 alpha-2 (ex. "FR") en emoji drapeau 🇫🇷. */
export function countryCodeToFlag(code?: string): string {
  if (!code || code.length !== 2) return "🏳️";
  const base = 0x1f1e6;
  const offset = "A".charCodeAt(0);
  const upper = code.toUpperCase();
  return String.fromCodePoint(
    base + upper.charCodeAt(0) - offset,
    base + upper.charCodeAt(1) - offset
  );
}
